import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalize a US phone number to strict E.164 (+1XXXXXXXXXX).
 * Strips formatting and an optional leading country code. Returns an empty
 * string when the input doesn't contain a valid 10-digit US number, so callers
 * can detect invalid input instead of sending Uber a malformed phone.
 */
export function toE164US(input: string | undefined | null): string {
  if (!input) return '';
  let digits = input.replace(/\D/g, '');
  // Drop a leading US country code if present (11 digits starting with 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return '';
  return `+1${digits}`;
}

/**
 * Flatten an order's stateHistory map into { numericKey: entry } shape.
 *
 * Handles both formats found in the database:
 *  - current: flat entries keyed by a numeric string (no dots)
 *  - legacy:  the engine briefly wrote keys like "1781200383633.123",
 *    which Firestore split into nested maps: { "1781200383633": { "123": entry } }
 */
export function flattenStateHistory(
  history: Record<string, any> | undefined | null
): Record<string, any> {
  if (!history) return {};
  const flat: Record<string, any> = {};
  for (const [key, value] of Object.entries(history)) {
    if (!value || typeof value !== 'object') continue;
    if (value.toState) {
      flat[key] = value;
    } else {
      // Legacy nested map — re-join outer ms key with the 3-digit suffix
      for (const [subKey, entry] of Object.entries(value as Record<string, any>)) {
        if (entry && typeof entry === 'object' && entry.toState) {
          flat[`${key}${subKey}`] = entry;
        }
      }
    }
  }
  return flat;
}

/**
 * Format an ISO timestamp (e.g. Uber pickup_eta / dropoff_eta) as a clock
 * time like "2:41 PM". Returns null for missing/invalid input.
 */
export function formatEtaClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * Relative minutes until an ISO timestamp, as a short label like "~8 min" or
 * "any moment". Returns null for missing/invalid input.
 */
export function formatEtaRelative(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  if (mins <= 1) return 'any moment';
  return `~${mins} min`;
}

/**
 * Build a "stop X of N" label when an Uber delivery is batched (courier
 * carrying multiple deliveries). Returns null when the order isn't batched.
 * `total` may be missing, in which case we show just the sequence.
 */
export function formatBatchStop(
  batchId: string | null | undefined,
  sequence: number | null | undefined,
  total: number | null | undefined,
): string | null {
  const seq = typeof sequence === 'number' ? sequence : null;
  // Only batched when there's a batch id AND more than one stop (or a seq > 1).
  const isBatched = !!batchId && ((typeof total === 'number' && total > 1) || (seq !== null && seq > 1));
  if (!isBatched || seq === null) return null;
  if (typeof total === 'number' && total > 1) return `Stop ${seq} of ${total}`;
  return `Stop ${seq}`;
}

export function isSaleActive(product: {
  price: number;
  compareAtPrice?: number | null;
  saleSchedule?: { startDate: string; endDate: string } | null;
}): boolean {
  if (!product.compareAtPrice || product.compareAtPrice <= product.price) {
    return false;
  }
  if (!product.saleSchedule) return true;
  const now = Date.now();
  const start = new Date(product.saleSchedule.startDate).getTime();
  const end = new Date(product.saleSchedule.endDate).getTime();
  return now >= start && now <= end;
}

export function getEffectivePrice(product: {
  price: number;
  compareAtPrice?: number | null;
  saleSchedule?: { startDate: string; endDate: string } | null;
}): { currentPrice: number; originalPrice: number | null; discountPercent: number } {
  if (isSaleActive(product)) {
    return {
      currentPrice: product.price,
      originalPrice: product.compareAtPrice!,
      discountPercent: Math.round(
        ((product.compareAtPrice! - product.price) / product.compareAtPrice!) * 100
      ),
    };
  }
  return { currentPrice: product.price, originalPrice: null, discountPercent: 0 };
}

